import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, useLocation } from "react-router-dom";
import Layout from "./layouts";
import Login from "./features/login";
import Loading from "components/Loading";
import { Modal, Spin, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import CheckAccessRoute from "./hoc/CheckAccessRoute";
import NotFound from "features/not-found";
import AccessDenied from "features/access-denied";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { selectLang } from "store/lang/langSlice";
import enMessages from "locales/en/messages.json";
import viMessages from "locales/vi/messages.json";
import kmMessages from "locales/km/messages.json";
import zhMessages from "locales/zh/messages.json";
import { en, vi, zh, km } from "make-plural/plurals";
import zhCN from "antd/lib/locale-provider/zh_CN";
import enUS from "antd/lib/locale-provider/en_US";
import kmKH from "antd/lib/locale-provider/km_KH";
import viVN from "antd/lib/locale-provider/vi_VN";
import { ConfigProvider } from "antd";
import { t } from "@lingui/macro";
import { RouteEl } from "types";

import routesArr from "./routes/routes";
import Dashboard from "features/dashboard";
import { getMyProfileAsync, selectAuth } from "store/auth/authSlice";
import { translate } from "utils";
import { EXPIRED_SESSION_MESSAGE_KEY } from "constants/constants";

const AllRoute: RouteEl[] = [];

routesArr.forEach(route => {
	if (route.component) {
		AllRoute.push(route);
	}
	if (route.children && route.children.length) {
		route.children.forEach(child => {
			if (child.children && child.children.length > 0) {
				child.children.forEach(childItem => {
					if (childItem.children && childItem.children.length) {
						childItem.children.forEach(item => AllRoute.push(item));
					}
					AllRoute.push(childItem);
				});
			}
			AllRoute.push(child);
		});
	}
});

declare global {
	interface Window {
		dialogConfirm: (
			title: string,
			content: string,
			callback: (isOk: boolean) => void,
			confirmStatus?: boolean,
			contentSlot?: ReactElement,
		) => void;
	}
}

window.dialogConfirm = (
	title: string,
	content: string,
	callback: (isOk: boolean) => void,
	confirmStatus?: boolean,
	contentSlot?: ReactElement,
) => {
	Modal.confirm({
		title: "",
		icon: null,
		content: (
			<div className="text-center">
				<div>
					<ExclamationCircleOutlined
						style={{ fontSize: "30px", color: "#faad14" }}
					/>
				</div>
				<p className="text-lg mt-2 font-bold">{title}</p>
				<p className="mt-3">{content}</p>
				{contentSlot && <div className="w-full mt-3">{contentSlot}</div>}
			</div>
		),
		okText: confirmStatus ? t`Confirm` : t`Yes`,
		cancelText: confirmStatus ? t`Cancel` : t`No`,
		onOk: () => {
			callback(true);
		},
		onCancel: () => {
			callback(false);
		},
	});
};

const languages = {
	zh: zhCN,
	en: enUS,
	vi: viVN,
	km: kmKH,
};

i18n.load({
	en: enMessages,
	vi: viMessages,
	km: kmMessages,
	zh: zhMessages,
});

i18n.loadLocaleData("en", { plurals: en });
i18n.loadLocaleData("vi", { plurals: vi });
i18n.loadLocaleData("zh", { plurals: zh });
i18n.loadLocaleData("km", { plurals: km });

function App() {
	const dispatch = useDispatch();
	const lang = useSelector(selectLang);
	const auth = useSelector(selectAuth);
	const [isLoading, setIsLoading] = useState(true);
	const location = useLocation();

	useEffect(() => {
		window.scrollTo({ top: 0 });
	}, [location.pathname]);

	useEffect(() => {
		i18n.activate(lang);
	}, [lang]);

	useEffect(() => {
		// Avoid call duplicate API my-profile
		if (auth?.jwt) {
			if (location.pathname !== "/my-account") {
				dispatch(
					getMyProfileAsync({
						returnAction: () => {
							setIsLoading(false);
						},
					}),
				);
			} else setIsLoading(false);
		} else {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		setTimeout(() => {
			const errorMessage = sessionStorage.getItem(EXPIRED_SESSION_MESSAGE_KEY);
			if (errorMessage) {
				message.error(translate(errorMessage));
				sessionStorage.setItem(EXPIRED_SESSION_MESSAGE_KEY, "");
				setIsLoading(false);
			}
		}, 100);
	}, [auth]);

	if (isLoading)
		return (
			<div className="flex h-screen">
				<Spin className="m-auto" size="large" />
			</div>
		);

	return (
		<I18nProvider i18n={i18n}>
			<ConfigProvider locale={languages[lang]}>
				<Loading />
				<Routes>
					<Route path="/login" element={<Login />} />
					<Route path="/" element={auth ? <Layout /> : <Login />}>
						<Route
							index
							element={
								<CheckAccessRoute>
									<Dashboard />
								</CheckAccessRoute>
							}
						/>
						{AllRoute.map(route => {
							const { label, path, component } = route;
							return (
								<Route
									key={label}
									path={path}
									element={
										<React.Suspense
											fallback={
												<div className="w-full h-full bg-white mt-5 p-5 text-center fade-in">
													{t`Loading`}...
												</div>
											}
										>
											<CheckAccessRoute>{component}</CheckAccessRoute>
										</React.Suspense>
									}
								/>
							);
						})}
					</Route>
					<Route path="/access-denied" element={<AccessDenied />} />
					<Route path="*" element={<NotFound />} />
				</Routes>
			</ConfigProvider>
		</I18nProvider>
	);
}

export default App;
